import {
  FilePreviewModal,
  Icon,
  IPreviewFileProps,
  MapSection,
  NotFoundPage,
  Spinner,
} from "dash-ui";
import {
  Broker,
  IdxSettings,
  Property,
  useGetIdxEmbeddedPropertyFeedQuery,
  useGetUnitsQuery,
} from "gql/generated.graphql";
import { useTranslation } from "react-i18next";
import { camelCase } from "lodash";
import {
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
  FiMapPin,
} from "react-icons/fi";
import { Link, useParams, useLocation } from "react-router-dom";
import { PropertyImageDisplay } from "v2/components/properties/property-image-display-component";
import DashLogo from "assets/images/png/SignInLogo.png";
import { useLayoutEffect, useMemo, useState } from "react";
import { ExpandableText } from "v2/components/property-general-info-section";
import IDXContactForm from "../../components/idx/idx-contact-form";
import { IdxFeedTypeEnum, ProjectStatusesEnum } from "dash-common";
import { lighten } from "polished";

type InventoryTabEnum = "MODEL" | "FOR_SALE";

// Main Component
export function IdxEmbeddedPropertyPage() {
  const { t } = useTranslation(["forms"]);

  const location = useLocation();

  // The state of the search page before clicking on the property
  const searchState = location?.state;

  // React States
  const [inventoryTab, setInventoryTab] = useState<InventoryTabEnum>("MODEL");
  const [inventoryPage, setInventoryPage] = useState<number>(1);
  const [fileToPreview, setFileToPreview] = useState<IPreviewFileProps | null>(
    null
  );

  // Route Params
  const params = useParams();
  const idxFeedId = params.idxFeedId || "";
  const propertyId = params.propertyId || "";

  // Property Data
  const getPropertyQuery = useGetIdxEmbeddedPropertyFeedQuery({
    variables: {
      input: {
        idxFeedId,
        propertyId,
      },
    },
  });

  const { data: unitsResult, loading: isLoadingUnits } = useGetUnitsQuery({
    variables: {
      pagination: { skip: (inventoryPage - 1) * 5, limit: 5 },
      filter: { propertyId, inventoryType: inventoryTab },
    },
  });

  const { unitsData, unitsTotal } = useMemo(
    () => ({
      unitsData: unitsResult?.getUnits?.collection || [],
      unitsTotal: unitsResult?.getUnits?.totalCount || 0,
    }),
    [unitsResult]
  );

  useLayoutEffect(() => {
    setInventoryPage(1);
  }, [inventoryTab]);

  // Derived states
  const idxFeed = getPropertyQuery.data?.getIdxEmbeddedPropertyFeed.idxFeed;
  const property = getPropertyQuery.data?.getIdxEmbeddedPropertyFeed
    .property as Property;
  const amenities =
    getPropertyQuery.data?.getIdxEmbeddedPropertyFeed.propertyAmenities || [];
  const broker = getPropertyQuery?.data?.getIdxEmbeddedPropertyFeed
    .broker as Broker;
  const idxSettings =
    (getPropertyQuery?.data?.getIdxEmbeddedPropertyFeed
      .idxSettings as IdxSettings) || null;

  // Converts plain text to HTML code to check if there is any content other than code
  const htmlParser = new DOMParser();
  const overviewHtmlContent = htmlParser.parseFromString(
    property?.overview || "",
    "text/html"
  );
  const overviewTextContent = overviewHtmlContent.body.textContent?.trim();

  // Branding Colors
  const mainColor = idxSettings?.branding.mainColor || "#1b75bb";
  const textColor = idxSettings?.branding.textColor || "#334155";
  const backgroundColor = idxSettings?.branding.backgroundColor || "";

  const transactionTypeText = t(
    `forms:labels.propertyTransactionTypes.${camelCase(
      property?.transactionType
    )}`
  );

  // Render loading state
  if (getPropertyQuery.loading) {
    return <Spinner fullContent />;
  }

  // Render Error State
  if (getPropertyQuery.error || !property) {
    return (
      <NotFoundPage
        onRedirect={() =>
          (window.location.href = "https://www.dashtodash.com/")
        }
      />
    );
  }

  // HTML Component
  return (
    <>
      <div
        className="flex flex-col max-w-screen h-screen items-center overflow-y-auto"
        style={{ backgroundColor: backgroundColor || "none", color: textColor }}
      >
        <div className="flex flex-col w-full max-w-screen-lg gap-12 py-10 px-4">
          {/* Breadcrumbs */}
          {idxFeed?.type === IdxFeedTypeEnum.PORTFOLIO && (
            <div className="flex">
              <Link
                to={`/exchange/embedded/${idxFeedId}/portfolio/${idxFeed.portfolioFolderId}`}
                className="h-8 px-4 rounded-lg flex items-center gap-2 text-sm border"
                style={{
                  borderColor: mainColor,
                  color: mainColor,
                }}
                state={searchState}
              >
                <FiChevronLeft />
                <span>Back to portfolio</span>
              </Link>
            </div>
          )}
          {idxFeed?.type === IdxFeedTypeEnum.SEARCH_BAR && (
            <div className="flex">
              <Link
                to={`/exchange/embedded/${idxFeedId}/search-bar/`}
                className="h-8 px-4 rounded-lg flex items-center gap-2 text-sm border"
                style={{ borderColor: mainColor, color: mainColor }}
                state={searchState}
              >
                <FiChevronLeft />
                <span>Back</span>
              </Link>
            </div>
          )}

          {/* Slideshow Section */}
          <PropertyImageDisplay
            images={property.slideshowMedia}
            mainColor={mainColor}
          />

          {/* Main Info Section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold">{property.name}</h1>
              {property.transactionType && (
                <span
                  className="text-xs py-0.5 px-2 rounded text-white"
                  style={{
                    backgroundColor: lighten(0.1, mainColor),
                  }}
                >
                  {transactionTypeText}
                </span>
              )}
              {property.marketArea && (
                <span
                  className="text-xs py-0.5 px-2 rounded text-white"
                  style={{
                    backgroundColor: mainColor,
                  }}
                >
                  {property.marketArea?.name}
                </span>
              )}
            </div>
            <div className="flex items-center flex-wrap gap-2">
              {property.priceRange && (
                <span>
                  ${((property.priceRange?.min || 0) / 1000000).toFixed(1)}M - $
                  {((property.priceRange?.max || 0) / 1000000).toFixed(1)}M
                </span>
              )}
              {property.averageBedroomsNumberValue && (
                <>
                  <div
                    className="h-4 w-0.5"
                    style={{ backgroundColor: mainColor }}
                  />
                  <div className="flex items-center gap-2">
                    <Icon name="bedroom" size={24} />
                    <span>
                      {`${property.averageBedroomsNumberValue?.min} - ${property.averageBedroomsNumberValue?.max}`}
                    </span>
                  </div>
                </>
              )}
              {property.averageBathroomsNumberValue && (
                <>
                  <div
                    className="h-4 w-0.5"
                    style={{ backgroundColor: mainColor }}
                  />
                  <div className="flex items-center gap-2">
                    <Icon name="bathroom" size={24} />
                    <span>
                      {`${property.averageBathroomsNumberValue?.min} - ${property.averageBathroomsNumberValue?.max}`}
                    </span>
                  </div>
                </>
              )}
              <div
                className="h-4 w-0.5"
                style={{ backgroundColor: mainColor }}
              />
              <div></div>
              <div className="flex items-center gap-2">
                <FiMapPin />
                <span>{`${property.location.address?.street}, ${property.location.address?.city}, ${property.location.address?.state} ${property.location.address?.zipCode}`}</span>
              </div>
            </div>
            <div>
              <div
                className="flex items-center text-sm"
                style={{ color: textColor }}
              >
                <span className="capitalize">
                  {property.projectStatus !== ProjectStatusesEnum.COMPLETED &&
                    property.projectStatus?.toLowerCase().split("_").join(" ")}
                </span>
                {property.yearBuilt &&
                property.projectStatus === ProjectStatusesEnum.COMPLETED ? (
                  <span>Completed in {property.yearBuilt}</span>
                ) : (
                  <span>, Est. Completion in {property.yearBuilt}</span>
                )}
              </div>
            </div>
          </div>

          {/* Property Overview */}
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-bold" style={{ color: textColor }}>
              Property Overview
            </h2>

            {overviewTextContent && (
              <ExpandableText
                text={property.overview || ""}
                mainColor={mainColor}
              />
            )}
            {!overviewTextContent && <p>N/A</p>}
          </div>

          {/* Features and Amenities Section */}
          <div className="flex flex-col gap-4">
            <h2 className="text-lg font-bold">Features and Amenities</h2>
            {amenities.length < 1 && <span>N/A</span>}
            {amenities.length >= 1 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                {amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <img className="size-6" src={amenity.imageUrl || ""} />
                    <span>{amenity.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Inventory Section */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Inventory Section</h2>
            </div>
            <div className="shadow rounded-lg border bg-white">
              <div className="flex items-center border-b">
                <button
                  className="h-10 px-8"
                  style={{
                    borderBottom:
                      inventoryTab === "MODEL"
                        ? `2px solid ${mainColor}`
                        : "none",
                    color: inventoryTab === "MODEL" ? mainColor : "inherit",
                  }}
                  onClick={() => setInventoryTab("MODEL")}
                >
                  Model
                </button>
                <button
                  className={`h-10 px-8 ${
                    inventoryTab === "FOR_SALE"
                      ? "border-b-2 border-b-sky-600 text-sky-600"
                      : ""
                  }`}
                  style={{
                    borderBottom:
                      inventoryTab === "FOR_SALE"
                        ? `2px solid ${mainColor}`
                        : "none",
                    color: inventoryTab === "FOR_SALE" ? mainColor : "inherit",
                  }}
                  onClick={() => setInventoryTab("FOR_SALE")}
                >
                  {transactionTypeText}
                </button>
              </div>
              <div className="flex flex-col overflow-auto">
                {isLoadingUnits && (
                  <div className="h-40 flex items-center justify-center gap-2 animate-pulse">
                    <FiLoader className="animate-spin" />
                    <p className="text-sm" style={{ color: textColor }}>
                      Loading units...
                    </p>
                  </div>
                )}
                {!isLoadingUnits && !unitsData?.length && (
                  <div
                    className="h-40 flex flex-col items-center justify-center gap-2"
                    style={{ color: mainColor }}
                  >
                    <Icon name="building" size={40} className={`opacity-30`} />
                    <p className="text-sm">No units available.</p>
                  </div>
                )}
                {!!unitsData?.length &&
                  unitsData?.map((unit, index) => (
                    <div key={index} className="flex items-center p-4 gap-4">
                      <img
                        className="size-20 bg-slate-200 rounded-lg object-contain"
                        src={
                          unit.floorPlanFile?.thumbnailFileUrl ||
                          "/images/dash_logo_compact.png"
                        }
                      />
                      <div className="flex flex-row w-full justify-between items-center flex-wrap">
                        <div className="flex flex-col ml-4 gap-1">
                          <p className="font-bold text-lg">{unit.name}</p>
                          <div className="flex items-center">
                            <div className="flex items-center gap-1">
                              <Icon name="bedroom" size={24} />
                              <span>
                                {`${
                                  unit.averageBedroomsNumber.min || "N/A"
                                } - ${unit.averageBedroomsNumber.max || "N/A"}`}
                              </span>
                            </div>
                            <div className="h-4 mx-4 bg-slate-300 w-0.5" />
                            <div className="flex items-center gap-1">
                              <Icon name="bathroom" size={24} />
                              <span>{`${
                                unit.averageBathroomsNumber.min || "N/A"
                              } - ${
                                unit.averageBathroomsNumber.max || "N/A"
                              }`}</span>
                            </div>
                          </div>
                          <div className="flex text-sm truncate">
                            <div className="flex items-center gap-1">
                              <span className="" style={{ color: textColor }}>
                                Priced from:
                              </span>
                              <span className="font-bold">
                                {!unit.startingPrice && "N/A"}
                                {!!unit.startingPrice &&
                                  `${(unit.startingPrice / 1000000).toFixed(
                                    1
                                  )}M`}
                              </span>
                            </div>
                            <div className="h-4 mx-2 bg-slate-300 w-0.5" />
                            <div className="flex items-center gap-1">
                              <span className="" style={{ color: textColor }}>
                                Addl. Rooms:
                              </span>
                              <span className="font-bold">
                                {unit.customRooms || "N/A"}
                              </span>
                            </div>
                            <div className="h-4 mx-2 bg-slate-300 w-0.5" />
                            <div className="flex items-center gap-1">
                              <span className="" style={{ color: textColor }}>
                                TOTAL SQFT:
                              </span>
                              <span className="font-bold capitalize">
                                {!unit.averageTotalSqft && "N/A"}
                                {!!unit.averageTotalSqft &&
                                  `${unit.averageTotalSqft.min} - ${unit.averageTotalSqft.max}`}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          disabled={!unit.floorPlanFile}
                          className="ml-4 mt-2 h-8 px-4 rounded-lg disabled:invisible text-white"
                          style={{ backgroundColor: mainColor }}
                          onClick={() =>
                            setFileToPreview(unit.floorPlanFile || null)
                          }
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
              {!!unitsData?.length && (
                <div className="h-12 flex items-center justify-between px-4 border-t">
                  <p className="" style={{ color: textColor }}>
                    {`Showing ${inventoryPage * 5 - 4} - ${
                      unitsTotal && inventoryPage * 5 > unitsTotal
                        ? unitsTotal
                        : inventoryPage * 5
                    } of ${unitsTotal}`}
                  </p>
                  {unitsTotal > 5 && (
                    <div className="flex items-center gap-1">
                      <button
                        disabled={inventoryPage === 1}
                        onClick={() => setInventoryPage(inventoryPage - 1)}
                        style={{ color: mainColor }}
                        className="size-8 rounded hover:bg-gray-50 bg-opacity-30 flex items-center justify-center  hover:text-white disabled:opacity-30"
                      >
                        <FiChevronLeft />
                      </button>
                      <button
                        disabled={
                          !!unitsTotal && inventoryPage * 5 >= unitsTotal
                        }
                        onClick={() => setInventoryPage(inventoryPage + 1)}
                        style={{ color: mainColor }}
                        className="size-8 rounded hover:bg-gray-50 bg-opacity-30 flex items-center justify-center  hover:text-white disabled:opacity-30"
                      >
                        <FiChevronRight />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <FilePreviewModal
              isOpened={!!fileToPreview}
              onClose={() => setFileToPreview(null)}
              files={fileToPreview ? [fileToPreview] : []}
              isDownloadButtonVisible={false}
              isExchange={true}
            />
          </div>

          {/* Additional Information */}
          <div className="flex flex-col gap-4">
            <h2 className="text-lg font-bold">Additional Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Number of Units
                </label>
                <p>{property.numberOfUnits || "N/A"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Property Status
                </label>
                <p className="capitalize">
                  {property.projectStatus?.toLowerCase().split("_").join(" ") ||
                    "N/A"}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Year Built
                </label>
                <p>{property.yearBuilt || "N/A"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Address
                </label>
                {!property.location.address && <p>N/A</p>}
                {property.location.address && (
                  <p>
                    <span>{`${property.location.address?.street}, ${property.location.address?.city}, ${property.location.address?.state} ${property.location.address?.zipCode}`}</span>
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Number of Stories
                </label>
                <p>{property.stories || "N/A"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Number of Towers
                </label>
                <p>{property.numberOfTowers || "N/A"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Property Type
                </label>
                {property.buildingTypes.length < 1 && <span>N/A</span>}
                {property.buildingTypes.length >= 1 && (
                  <p>
                    {property.buildingTypes.map((type, index) => (
                      <span key={index}>
                        {t(`labels.buildingTypes.${camelCase(type)}`)}
                        {/* Add comma between building types */}
                        {index !== property.buildingTypes.length - 1
                          ? ", "
                          : ""}
                      </span>
                    ))}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Lead Architect
                </label>
                <p>{property.leadArchitect || "N/A"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Landscape Architect
                </label>
                <p>{property.landscapeArchitect || "N/A"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Interior Design
                </label>
                <p>{property.interiorDesigner || "N/A"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs " style={{ color: textColor }}>
                  Taxes & HOA
                </label>
                <p>{property.taxInformation || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Map Section */}
          <div className="flex flex-col gap-4">
            <h2 className="font-bold text-lg">Map</h2>
            <MapSection property={property} />
          </div>

          {/* Contact Section */}
          {broker && (
            <div className="flex flex-col gap-4">
              <h2 className="font-bold text-lg">Contact</h2>
              <IDXContactForm
                propertyName={property?.name || ""}
                broker={broker}
                branding={{ mainColor, textColor, backgroundColor }}
              />
            </div>
          )}

          <footer className="flex items-center justify-center mt-8">
            <a
              style={{ backgroundColor: mainColor, color: mainColor }}
              className="flex items-center justify-center bg-sky-600 rounded-full py-2 px-4 flex-wrap gap-2 shrink-0 min-w-max"
              href="https://dashtodash.com/"
              target="_blank"
            >
              <span className="text-sm text-white font-[montserrat]">
                Powered By
              </span>
              <img src={DashLogo} alt="DASH" className="h-4" />
            </a>
          </footer>
        </div>
      </div>
    </>
  );
}
